import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Seo from "@/components/Seo";
import ShortAnswer from "@/components/ShortAnswer";
import { AUTHOR } from "@/data/author";
import { HUB_PATHS, SITUATIONS, SITUATION_GROUPS, VERDICTS, CHECKED_ON } from "@/data/equality";
import { Hero, breadcrumb, linkClass } from "./shared";

const TONE = {
  yes: "bg-ink text-paper",
  maybe: "bg-pink text-ink",
  no: "bg-ink/10 text-ink",
};

function Situation({ s }) {
  const verdict = VERDICTS[s.verdict];
  return (
    <details id={s.id} data-testid={`situation-${s.id}`} className="group border-2 border-ink bg-white open:shadow-[6px_6px_0_0_#F472B6]">
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 p-5 sm:p-6">
        <span className="font-display text-lg sm:text-xl font-semibold tracking-tight">{s.title}</span>
        <span className={`max-w-full px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] ${TONE[verdict.tone]}`}>{verdict.label}</span>
      </summary>
      <div className="border-t-2 border-ink/10 p-5 sm:p-6">
        <p className="eyebrow text-ink/50">{s.characteristic}</p>
        <div className="mt-3 space-y-3 text-base leading-relaxed text-ink/80">
          {s.answer.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
        <div className="mt-5 border-l-4 border-pink bg-pink/10 p-4 text-sm leading-relaxed text-ink/80">
          <span className="font-semibold">Best practice either way: </span>
          {s.bestPractice}
        </div>
        <p className="mt-4 text-xs leading-relaxed text-ink/55">
          <span className="font-semibold">Law: </span>
          {s.law.map((l, i) => (
            <span key={l.label}>
              {i > 0 && "; "}
              <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline decoration-pink underline-offset-2 hover:text-ink">
                {l.label}
              </a>
            </span>
          ))}
        </p>
      </div>
    </details>
  );
}

export default function IsItProtected() {
  const origin = window.location.origin;
  const pageUrl = `${origin}${HUB_PATHS.checker}`;
  const [q, setQ] = useState("");

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return SITUATIONS;
    return SITUATIONS.filter((s) =>
      [s.title, s.characteristic, ...s.answer].join(" ").toLowerCase().includes(term),
    );
  }, [q]);

  return (
    <main data-testid="is-it-protected">
      <Seo
        title="Is It a Protected Characteristic? Equality Act Checker (UK) | Electra HR"
        siteName="Electra HR"
        description={`Is mental health, menopause, ADHD, autism, childcare or a belief protected under the Equality Act 2010? Check ${SITUATIONS.length} common situations, the legal test for each, and best practice for employers and employees.`}
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": pageUrl,
              url: pageUrl,
              name: "Is it protected? Equality Act checker",
              isPartOf: { "@id": `${origin}/#website` },
              publisher: { "@id": `${origin}/#organization` },
              reviewedBy: { "@type": "Person", name: AUTHOR.name, jobTitle: "Solicitor" },
              dateModified: CHECKED_ON.iso,
            },
            {
              "@type": "FAQPage",
              mainEntity: SITUATIONS.map((s) => ({
                "@type": "Question",
                name: `Is "${s.title.toLowerCase()}" protected under the Equality Act 2010?`,
                acceptedAnswer: { "@type": "Answer", text: `${VERDICTS[s.verdict].label}. ${s.answer.join(" ")}` },
              })),
            },
            breadcrumb(origin, [
              { name: "Home", path: "/" },
              { name: "Equality at work", path: HUB_PATHS.home },
              { name: "Is it protected?", path: HUB_PATHS.checker },
            ]),
          ],
        }}
      />

      <Hero
        testid="checker-hero"
        eyebrow="Equality at work · Checker"
        title="Is it protected?"
        accent="The grey areas, settled."
        intro="Most disputes about discrimination start with someone being sure a thing is, or isn't, covered. Pick the situation and see what the Equality Act 2010 actually says, and what a fair employer does either way."
      />

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x max-w-4xl">
          <ShortAnswer path={HUB_PATHS.checker} className="mb-10" />

          <label htmlFor="checker-search" className="eyebrow text-ink/55">Search the situations</label>
          <div className="mt-3 flex items-center gap-3 border-2 border-ink bg-white px-4 py-3">
            <Search size={18} className="text-ink/50" />
            <input
              id="checker-search"
              data-testid="checker-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. ADHD, menopause, childcare, vegan"
              className="w-full bg-transparent text-base outline-none placeholder:text-ink/40"
            />
          </div>
          {shown.length === 0 && (
            <p data-testid="checker-no-results" className="mt-6 text-base text-ink/70">
              Nothing matches that. If it isn't one of the nine characteristics on the{" "}
              <Link to={HUB_PATHS.home} className={linkClass}>Equality at work page</Link>, it is only covered if it fits within one of them.
            </p>
          )}

          {SITUATION_GROUPS.map((g) => {
            const items = shown.filter((s) => s.group === g);
            if (!items.length) return null;
            return (
              <section key={g} className="mt-12">
                <h2 className="font-display text-2xl font-semibold tracking-tight">{g}</h2>
                <div className="mt-5 space-y-4">
                  {items.map((s) => (
                    <Situation key={s.id} s={s} />
                  ))}
                </div>
              </section>
            );
          })}

          <div className="mt-16 grid gap-4 sm:grid-cols-2">
            <Link to={HUB_PATHS.employers} className="border-2 border-ink bg-white p-6 font-display text-lg font-semibold hover:shadow-[6px_6px_0_0_#F472B6]">
              Reading this as an employer? Go to the employers' hub
            </Link>
            <Link to={HUB_PATHS.employees} className="border-2 border-ink bg-white p-6 font-display text-lg font-semibold hover:shadow-[6px_6px_0_0_#F472B6]">
              Reading this about your own job? Go to the hub for people at work
            </Link>
          </div>

          <p className="mt-10 text-sm leading-relaxed text-ink/60">
            This checker explains the law in general terms for Great Britain. It is not advice on a particular situation, where the facts decide the outcome. Law checked against the current revised text on legislation.gov.uk on {CHECKED_ON.label}, by {AUTHOR.name}, {AUTHOR.credential}.
          </p>
        </div>
      </section>
    </main>
  );
}
